import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { NgCommonModule, FromNowAsyncPipe } from '@principle-theorem/ng-shared';
import { ReportRun } from '@principle-theorem/principle-core';
import {
  type ICustomReport,
  IReportRun,
  REPORT_RUN_RETENTION_DAYS,
} from '@principle-theorem/principle-core/interfaces';
import {
  FieldValue,
  Firestore,
  filterUndefined,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import {
  ReportRunHistoryDialogComponent,
  type IReportRunHistoryDialogData,
} from './report-run-history-dialog.component';

const SIDEBAR_RUN_LIMIT = 10;

@Component({
  selector: 'pr-report-run-history',
  templateUrl: './report-run-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, MatButtonModule, MatIcon, MatMenuModule, RouterModule, FromNowAsyncPipe],
})
export class ReportRunHistoryComponent {
  private _dialog = inject(MatDialog);
  private _allRuns$: Observable<WithRef<IReportRun>[]>;

  customReport = input<WithRef<ICustomReport>>();

  allRuns: Signal<WithRef<IReportRun>[]>;
  recentRuns = computed(() => {
    return this.allRuns()
      .filter((run) => ReportRun.isViewable(run))
      .slice(0, SIDEBAR_RUN_LIMIT);
  });

  totalRunCount = computed(() => this.allRuns().length);
  hasMoreRuns = computed(() => this.totalRunCount() > SIDEBAR_RUN_LIMIT);

  historyBasePath = computed(() => {
    const report = this.customReport();
    if (!report) {
      return [];
    }
    return ['/', 'reporting', 'custom-reports', report.ref.id, 'history'];
  });

  constructor() {
    this._allRuns$ = toObservable(this.customReport).pipe(
      filterUndefined(),
      switchMap((report) => ReportRun.all$(report))
    );
    this.allRuns = toSignal(this._allRuns$, { initialValue: [] });
  }

  async togglePin(run: WithRef<IReportRun>): Promise<void> {
    const pinned = !run.pinned;
    const newExpiresAt = pinned
      ? undefined
      : toTimestamp(
          moment().add(REPORT_RUN_RETENTION_DAYS, 'days')
        );
    await Firestore.patchDoc(run.ref, {
      pinned,
      expiresAt: newExpiresAt ?? FieldValue.delete(),
    });
  }

  async archiveRun(run: WithRef<IReportRun>): Promise<void> {
    await Firestore.patchDoc(run.ref, {
      archivedAt: toTimestamp(),
      pinned: false,
      expiresAt: FieldValue.delete(),
    });
  }

  openDialog(): void {
    this._dialog.open<
      ReportRunHistoryDialogComponent,
      IReportRunHistoryDialogData
    >(ReportRunHistoryDialogComponent, {
      width: '800px',
      data: {
        runs$: this._allRuns$,
        historyBasePath: this.historyBasePath(),
      },
    });
  }
}
